import {
  EXPLOITABILITY_FILTER_OPTIONS,
  SEVERITIES_FILTER_OPTIONS
} from "@/const/options.constants"
import * as filtersHelper from "@/helpers/filters.helper"
import { INEQUALITY_SYMBOL } from "@/organisms/SmartSearch/const/symbol.constants"
import { getSeverityFilter } from "@/pages/DiscoveryPage/utils/filters.utils"

import type { TFunction } from "@/hooks/useTranslation"
import type { Filters } from "@/organisms/SmartSearch/types/options.types"

export { EXPLOITABILITY_FILTER_OPTIONS, SEVERITIES_FILTER_OPTIONS }

export const getInitialValues = (props: { params: Record<string, any> }) =>
  props.params

export const getFilters = (props: {
  t: TFunction
  includeApplicationFilter?: boolean
}): Filters => {
  const { t } = props

  const filters = [
    filtersHelper.getIssueTypeFilter(t),
    filtersHelper.getPackageNameFilter(t),
    filtersHelper.getVulnerabilityIdFilter(t),
    filtersHelper.getCveIdFilter(t),
    filtersHelper.getHighProfileFilter(t),
    getSeverityFilter(),
    {
      key: "runtime",
      label: t("filters.runtime"),
      type: "single",
      order: 1,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO", disabled: true },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ],
      helper: [{ description: "Packages that are seen in runtime" }]
    },
    {
      key: "ingress",
      label: t("filters.ingress"),
      type: "single",
      order: 2,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "exploitMaturity",
      label: t("filters.exploitability"),
      type: "multiple",
      options: EXPLOITABILITY_FILTER_OPTIONS
    },
    filtersHelper.getCweIdFilter(t),
    {
      key: "cweDescription",
      label: t("filters.cweName").toLowerCase(),
      displayLabel: t("filters.cweName"),
      type: "input",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.cweName")
          }),
          description: 'Matches multiple cwe name (e.g. "Prototype Pollution")'
        }
      ]
    },
    {
      key: "environment",
      label: t("filters.environment"),
      type: "input",
      helper: [
        {
          label: "environment:value1,value2",
          description: "Equals value1 OR value2"
        }
      ]
    },
    {
      key: "namespace",
      label: t("filters.namespace"),
      type: "input",
      helper: [
        {
          label: "namespace:value1,value2",
          description: "Matches namespaces as a substring (e.g. users,system)"
        }
      ]
    },
    filtersHelper.getIssueStatusFilter(t),
    {
      key: "kodemScore",
      label: t("filters.kodemScore"),
      type: "inputNumber",
      validation: (value: number) =>
        !isNaN(value) && value >= 0 && value <= 1000,
      errorMessage: "Insert value between 0 to 1000",
      helper: [
        { description: "Is greater or equal to a number between 0 - 1000" }
      ]
    },
    {
      key: "fromBaseImage",
      label: t("filters.fromBaseImage"),
      order: 6,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "fromThirdParty",
      label: t("filters.fromThirdParty"),
      order: 7,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isDirect",
      label: t("filters.isDirect"),
      order: 4,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isIndirect",
      label: t("filters.isIndirect"),
      order: 5,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "introducedThrough",
      label: t("filters.introducedThrough"),
      type: "input",
      linkedKey: "introducedThrough",
      condition: "AND",
      helper: [
        {
          label: "introduced-through:value1,value2",
          description:
            "Matches introduced through paths which include the provided substrings. Accepts package names, base images and code weakness locations"
        }
      ]
    },
    {
      key: "notIntroducedThrough",
      label: `${INEQUALITY_SYMBOL}${t("filters.introducedThrough")}`,
      type: "input",
      linkedKey: "introducedThrough",
      inequality: true,
      helper: [
        {
          label: "!introduced-through:value1,value2",
          description:
            "Matches introduced through paths which does NOT include the provided substrings. Accepts package names, base images and code weakness locations"
        }
      ]
    },
    filtersHelper.getLabelsFilter(t),
    filtersHelper.getNotLabelsFilter(t),
    {
      key: "hasLabel",
      label: "has-label",
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" }
      ]
    },
    filtersHelper.getHasActionFilter(t),
    filtersHelper.getJiraIssueIdFilter(t),
    filtersHelper.getLanguagesFilter(t),
    filtersHelper.getHasFunctionEvidenceFilter(t),
    filtersHelper.getOsLanguagesFilter(t),
    filtersHelper.getDismissReasonFilter(t),
    filtersHelper.getDismissedByFilter(t),
    filtersHelper.getIsDevDependencyFilter(t),
    filtersHelper.getFixVersionFilter(t),
    filtersHelper.getKodemRemediationFilter(t),
    filtersHelper.getRemediationEffortFilter(t),
    filtersHelper.getFixableFilter(t),
    filtersHelper.getKaiInsightFilter(t),
    filtersHelper.getKaiDataFlowFilter(t),
    filtersHelper.getResolvedTime(t),
    filtersHelper.getDiscoveredTime(t),
    filtersHelper.getDismissedTime(t)
  ] as Filters
  if (props.includeApplicationFilter) {
    filters.push({
      key: "application",
      label: t("filters.application"),
      type: "input",
      helper: [
        {
          label: t("filters.helpers.application.label"),
          description: t("filters.helpers.application.description")
        }
      ]
    })
  }
  return filters
}
